import { useState, useEffect } from "react";
import api from "../../services/api";
import { toast } from "react-toastify";
import useWrapper from "../../hooks/useWrapper";
import useSuppliers from "../../hooks/api/useSuppliers";
import usePermission from "../../hooks/usePermission";
import ModalCustom from "./modal-template-default";
import Button from "../Button";
import Slct from "../Slct";
import Loading from "../Loading";
import PaymentDetails from "../PaymentDetails";
import {
  supplierAccountOptions,
  supplierPixOptions,
  withEmptyOption,
  NO_ACCOUNTS_LABEL,
  NO_PIX_KEYS_LABEL,
} from "../../util/paymentMethods";

/**
 * Informações de pagamento do título: onde depositar / qual chave PIX usar.
 * Os dados ficam congelados no título desde a criação — o botão de atualizar
 * é a única forma de trazer o cadastro atual do fornecedor, para que um
 * título antigo continue registrando o que foi combinado na época.
 */
const ModalBillToPayPaymentInfo = props => {
  const { show, onHide, negotiation, refreshCallback } = props;
  const { config } = useWrapper();
  const canUpdate = usePermission("financialCompany", "update");

  const suppliers = useSuppliers();
  const [current, setcurrent] = useState(null);
  const [accountId, setaccountId] = useState(null);
  const [pixId, setpixId] = useState(null);
  const [isWaitingResponse, setisWaitingResponse] = useState(false);

  useEffect(() => {
    if (!show) return;
    suppliers.load();
    setcurrent(negotiation);
    setaccountId(negotiation?.payment_account_id || null);
    setpixId(negotiation?.payment_pix_id || null);
    // recarrega apenas na abertura do modal ou troca de título
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, negotiation?.id]);

  const supplier = suppliers?.data?.find(__supplier => __supplier.id === current?.provider_id);
  const accountOptions = supplierAccountOptions(supplier);
  // mesma regra do modal de criação: conta de destino só em transferência
  const hasSupplierAccounts = current?.method_payment === "transferencia";
  const pixOptions = supplierPixOptions(supplier);
  const canChoosePix = current?.method_payment === "pix";

  const closeModal = () => {
    if (isWaitingResponse) return;
    onHide();
  };

  const refreshPaymentDetails = async () => {
    if (!canUpdate) return;
    setisWaitingResponse(true);
    try {
      const { data: _updated } = await api.put(
        `bill-to-pay-negotiations/payment-details/${current.id}`,
        { payment_account_id: accountId || null, payment_pix_id: pixId || null },
        config,
      );
      setcurrent({ ...current, ..._updated });
      toast.success("Dados de pagamento atualizados!");
      refreshCallback && refreshCallback();
    } catch (__err) {
      const _message = __err?.response?.data?.error?.message;
      toast.error(_message || "Problemas ao atualizar os dados de pagamento!");
      console.error("__err", __err);
    } finally {
      setisWaitingResponse(false);
    }
  };

  return (
    <ModalCustom show={show} onHide={closeModal} id="bill_to_pay_payment_info_container" disableClose={isWaitingResponse}>
      <ModalCustom.Header>
        <ModalCustom.Title>Informações de Pagamento</ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        <p className="small-label">
          <b>Fornecedor:</b> {current?.providers?.name || "-"}
        </p>
        <PaymentDetails payment={current} className="mt-2" />
        {canUpdate && (
          <div className="mt-3">
            {suppliers?.data ? (
              <>
                {canChoosePix && (
                  <div className="mb-2">
                    <label>Chave PIX</label>
                    <Slct
                      isDisabled={isWaitingResponse}
                      options={withEmptyOption(pixOptions, NO_PIX_KEYS_LABEL)}
                      value={pixId}
                      onChange={__ev => setpixId(__ev.value)}
                      placeholder="..."
                    />
                  </div>
                )}
                {hasSupplierAccounts && (
                  <>
                    <label>Conta para depósito</label>
                    <Slct
                      isDisabled={isWaitingResponse}
                      options={withEmptyOption(accountOptions, NO_ACCOUNTS_LABEL)}
                      value={accountId}
                      onChange={__ev => setaccountId(__ev.value)}
                      placeholder="..."
                    />
                  </>
                )}
                <p className="small-label mt-2 mb-0">
                  Atualizar substitui os dados acima pelo cadastro atual do fornecedor.
                </p>
              </>
            ) : (
              <Loading className="mb-2" />
            )}
          </div>
        )}
      </ModalCustom.Body>
      <ModalCustom.Footer>
        <Button disabled={isWaitingResponse} color="primary_ghost" onClick={closeModal}>
          Fechar
        </Button>
        {canUpdate && (
          <Button disabled={isWaitingResponse || !suppliers?.data} onClick={refreshPaymentDetails} loading={isWaitingResponse}>
            Atualizar com dados atuais do fornecedor
          </Button>
        )}
      </ModalCustom.Footer>
    </ModalCustom>
  );
};

export default ModalBillToPayPaymentInfo;
